import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users, CreditCard, TrendingUp, FlaskConical, Download,
  ShieldCheck, Search, RefreshCw, AlertCircle,
} from "lucide-react";

/** Admin dashboard — visible only to ADMIN_EMAILS. */
export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null); // null=loading, true/false
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ rows: [], total: 0 });
  const [payments, setPayments] = useState({ rows: [], total: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  // Gate
  useEffect(() => {
    api.get("/admin/check").then(r => setAllowed(Boolean(r.data?.is_admin)))
      .catch(() => setAllowed(false));
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, u, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users", { params: { q: q || undefined, limit: 100 } }),
        api.get("/admin/payments", { params: { limit: 50 } }),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setPayments(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (allowed) refresh(); /* eslint-disable-next-line */ }, [allowed]);

  const handleExtendTrial = async (user) => {
    const daysStr = window.prompt(
      `Extend trial for ${user.email} by how many days?`,
      "15"
    );
    if (!daysStr) return;
    const days = parseInt(daysStr, 10);
    if (!Number.isFinite(days) || days <= 0 || days > 365) {
      toast.error("Enter a number between 1 and 365");
      return;
    }
    try {
      const { data } = await api.post(
        `/admin/users/${user.user_id}/extend-trial`,
        { days }
      );
      toast.success(
        `Trial extended by ${data.days_added} days · new end: ${new Date(data.trial_end).toLocaleDateString()}`
      );
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not extend trial");
    }
  };

  if (allowed === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 dark:bg-zinc-950">
        <div className="text-xs font-mono text-zinc-500">checking access…</div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 dark:bg-zinc-950 px-4" data-testid="admin-forbidden">
        <div className="max-w-md w-full p-8 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
          <AlertCircle className="h-6 w-6 text-amber-500 mb-3" />
          <h1 className="text-xl font-sans font-bold tracking-tight">Admin access required</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2">
            You&apos;re signed in as <code>{user?.email || "guest"}</code>. This area is restricted.
          </p>
          <Button className="mt-5" onClick={() => navigate("/dashboard")} data-testid="admin-back-btn">
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100" data-testid="admin-page">
      {/* Header */}
      <header className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded bg-blue-600 grid place-items-center text-white">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">ResyNote</div>
              <div className="font-sans font-semibold text-sm">Admin · {user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="admin-refresh-btn">
              <RefreshCw className={`h-3.5 w-3.5 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>← App</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* KPI cards */}
        <Stats data={stats} />

        {/* Signup chart */}
        <SignupChart data={stats?.signups_chart || []} />

        {/* Users table */}
        <section data-testid="admin-users-section">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="font-sans font-semibold text-lg flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" /> Users
              <span className="text-xs font-mono text-zinc-500">({users.total})</span>
            </h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="search by email…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && refresh()}
                  className="pl-8 h-9 w-64"
                  data-testid="admin-users-search"
                />
              </div>
              <Button asChild variant="outline" size="sm" data-testid="admin-users-csv">
                <a href={`${BACKEND_URL}/api/admin/users.csv`}>
                  <Download className="h-3.5 w-3.5 mr-2" /> CSV
                </a>
              </Button>
            </div>
          </div>
          <UsersTable rows={users.rows} onExtendTrial={handleExtendTrial} />
        </section>

        {/* Payments */}
        <section data-testid="admin-payments-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-sans font-semibold text-lg flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-emerald-600" /> Recent payments
              <span className="text-xs font-mono text-zinc-500">({payments.total})</span>
            </h2>
            <Button asChild variant="outline" size="sm" data-testid="admin-payments-csv">
              <a href={`${BACKEND_URL}/api/admin/payments.csv`}>
                <Download className="h-3.5 w-3.5 mr-2" /> CSV
              </a>
            </Button>
          </div>
          <PaymentsTable rows={payments.rows} />
        </section>
      </main>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent = "blue", testId }) {
  const dot = {
    blue: "bg-blue-600", emerald: "bg-emerald-600",
    amber: "bg-amber-500", zinc: "bg-zinc-500",
  }[accent] || "bg-blue-600";
  return (
    <div className="p-5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />
        {label}
      </div>
      <div className="flex items-end gap-3 mt-2">
        <div className="text-3xl font-sans font-bold tracking-tight" data-testid={`${testId}-value`}>{value}</div>
        {Icon && <Icon className="h-4 w-4 text-zinc-300 dark:text-zinc-700 mb-1" />}
      </div>
      {sub && <div className="text-xs font-mono text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function Stats({ data }) {
  if (!data) {
    return <div className="text-sm text-zinc-500 font-mono">loading stats…</div>;
  }
  const { users, revenue, content } = data;
  return (
    <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-kpis">
      <Kpi icon={Users} label="Total users" value={users.total} sub={`+${users.last_7d} in 7d`} accent="blue" testId="kpi-total-users" />
      <Kpi icon={TrendingUp} label="Active subs" value={users.active_subscriptions} sub={`${users.trial} on trial`} accent="emerald" testId="kpi-active-subs" />
      <Kpi icon={Users} label="Last 30d signups" value={users.last_30d} accent="zinc" testId="kpi-30d-signups" />
      <Kpi icon={CreditCard} label="Revenue 30d" value={`₹${(revenue.last_30d_inr || 0).toLocaleString("en-IN")}`} sub={`${revenue.purchases_30d} purchases`} accent="emerald" testId="kpi-revenue" />
      <Kpi icon={Users} label="Trial expired" value={users.expired} accent="amber" testId="kpi-expired" />
      <Kpi icon={FlaskConical} label="Experiments" value={content.experiments} sub={`${content.projects} projects · ${content.labs} labs`} accent="zinc" testId="kpi-experiments" />
    </section>
  );
}

function SignupChart({ data }) {
  const max = useMemo(() => Math.max(1, ...data.map((d) => d.count)), [data]);
  return (
    <section className="p-5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md" data-testid="admin-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Signups · last 30 days</div>
        <div className="text-xs font-mono text-zinc-500">peak: {max}/day</div>
      </div>
      <div className="flex items-end gap-1 h-32">
        {data.map((d, i) => (
          <div
            key={d.day}
            className="flex-1 group relative"
            style={{ minWidth: 4 }}
          >
            <div
              className={`mx-auto rounded-t transition-colors ${
                d.count > 0 ? "bg-blue-600 hover:bg-blue-500" : "bg-zinc-100 dark:bg-zinc-800"
              }`}
              style={{
                height: `${(d.count / max) * 100}%`,
                minHeight: d.count > 0 ? 4 : 2,
                width: "70%",
              }}
              title={`${d.day}: ${d.count}`}
            />
            {(i === 0 || i === 14 || i === 29) && (
              <div className="text-[9px] font-mono text-zinc-400 mt-1 text-center">
                {d.day.slice(5)}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function UsersTable({ rows, onExtendTrial }) {
  if (!rows.length) {
    return <div className="text-sm text-zinc-500 font-mono p-6 border border-dashed border-zinc-200 dark:border-zinc-800 rounded">No users match.</div>;
  }
  return (
    <div className="overflow-x-auto bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-zinc-800">
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Role</th>
            <th className="px-4 py-3">Joined</th>
            <th className="px-4 py-3">Trial ends</th>
            <th className="px-4 py-3">Subscription</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {rows.map((u) => {
            const sub = u.subscription || {};
            const active = u.subscription_active;
            return (
              <tr key={u.user_id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40" data-testid={`admin-user-${u.user_id}`}>
                <td className="px-4 py-3 font-body">
                  <div className="font-medium">{u.email}</div>
                  {u.name && <div className="text-xs font-mono text-zinc-500">{u.name}</div>}
                </td>
                <td className="px-4 py-3 text-xs font-mono">
                  <span className={`px-2 py-0.5 rounded ${u.account_role === "supervisor" ? "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400"}`}>
                    {u.account_role || "member"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-500">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-500">
                  {u.trial_end ? new Date(u.trial_end).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-xs">
                  {active ? (
                    <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 font-mono">
                      {sub.plan_key || "active"}
                    </span>
                  ) : (
                    <span className="font-mono text-zinc-500">trial / free</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onExtendTrial(u)}
                    className="text-[10px] font-mono uppercase tracking-widest text-blue-600 hover:text-blue-700 hover:underline"
                    data-testid={`extend-trial-${u.user_id}`}
                    title="Extend trial by N days"
                  >
                    +Trial
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsTable({ rows }) {
  if (!rows.length) {
    return <div className="text-sm text-zinc-500 font-mono p-6 border border-dashed border-zinc-200 dark:border-zinc-800 rounded">No payments yet.</div>;
  }
  return (
    <div className="overflow-x-auto bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-zinc-800">
            <th className="px-4 py-3">Customer</th>
            <th className="px-4 py-3">Plan</th>
            <th className="px-4 py-3 text-right">Amount</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3">Order ID</th>
            <th className="px-4 py-3">Invoice</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {rows.map((r) => {
            const statusColor = {
              PAID: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
              FAILED: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300",
            }[r.status] || "bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400";
            return (
              <tr key={r.order_id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                <td className="px-4 py-3 font-mono text-xs">{r.customer_email || "—"}</td>
                <td className="px-4 py-3 text-xs font-mono">{r.plan_key}</td>
                <td className="px-4 py-3 text-right font-sans font-semibold">
                  ₹{(r.amount || 0).toLocaleString("en-IN")}
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className={`px-2 py-0.5 rounded font-mono ${statusColor}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-500">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-[10px] font-mono text-zinc-500 max-w-[180px] truncate" title={r.order_id}>
                  {r.order_id}
                </td>
                <td className="px-4 py-3 text-xs">
                  {r.invoice_sent ? (
                    <span className="text-emerald-600 font-mono">sent</span>
                  ) : (
                    <span className="text-zinc-400 font-mono">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
